import cn from 'classnames';
import React, {FC, forwardRef, useEffect, useImperativeHandle, useRef, useState} from 'react';
import Slider from 'react-slick';

import * as localizationKeys from '../../consts/localization/localizationKeys';
import domService from '../../services/domService';
import {getLocalizedStrings} from '../../services/localization/localizationService';
import log from '../../services/logger/log';
import helpers from '../../utils/helpers';
import styles from './Carousel.module.scss';
import CarouselArrow from './CarouselArrow/CarouselArrow';
import arrowStyles from './CarouselArrow/CarouselArrow.module.scss';
import {IBuildOption, ICarousel} from './CarouselTypes';

const afterChange = () => {
    try {
        const carousels = document.getElementsByClassName('slick-track');

        for (const carousel of carousels) {
            const sliders = carousel.querySelectorAll('.slick-slide');

            for (const slider of sliders) {
                const isSliderVisible = slider.getAttribute('aria-hidden') !== 'true';
                const buttons = slider.querySelectorAll('button, a'); // The active slide buttons are rendered as anchor

                for (const button of buttons) {
                    const initialTabIndex = button.getAttribute('data-tabindex');
                    const requiredTabIndex = isSliderVisible ? initialTabIndex || '0' : '-1';
                    const buttonTabIndex = button.getAttribute('tabindex');

                    if (buttonTabIndex !== requiredTabIndex) {
                        button.setAttribute('tabindex', requiredTabIndex);
                    }
                }
            }
        }
    } catch (e) {
        log.debug(`Carousel: afterChange error: ${e}`);
    }
};

const Carousel: FC<ICarousel> = forwardRef((props, ref) => {
    const {
        ariaLabelLeft,
        ariaLabelRight,
        arrowNextTestId,
        arrowPrevTestId,
        controlArrowClassName,
        controlArrowLeftClassName,
        controlArrowRightClassName,
        dotsClassName,
        dotsTestId,
        focusOnSelect,
        isAccessibilityEnabled = true,
        isAdaptiveHeight,
        isControlArrowVisible,
        nextPreviousTabIndex,
        isDotsFocusEnabled = true,
        isDotsVisible = true,
        isDraggable = true,
        isInfinite = false,
        isSwipeEnabled = true,
        onTransitionEnd,
        onAfterChange,
        options,
        children,
    } = props;
    const localizedStrings = getLocalizedStrings();
    const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
    const itemsCount = children?.length;
    const isFirstSlide = currentSlideIndex === 0;
    const isLastSlide = currentSlideIndex === itemsCount - 1;

    const sliderRef: any = useRef(null);

    useEffect(() => {
        if (sliderRef.current) {
            (() => onSliderInit())();
        }
    }, [sliderRef.current]);

    const onSlide = (index: number) => {
        setCurrentSlideIndex(index);
        helpers.runFunction(onTransitionEnd, index);
    };

    const getCustomPaging = (i: number) => {
        const currentDotNumber = i + 1;
        const ariaLabel = localizedStrings.formatString(
            localizedStrings[localizationKeys.CAROUSEL_DOTS_BTN_ARIA_LABEL],
            currentDotNumber,
            itemsCount
        );

        return (
            <button aria-label={ariaLabel} tabIndex={isDotsFocusEnabled ? 0 : -1}>
                {currentDotNumber}
            </button>
        );
    };

    const onSliderInit = () => {
        afterChange();
    };

    const addDotsTestIDs = () => {
        try {
            if (dotsTestId) {
                const dots = domService.getAllElementsBySelector(`.${styles.Dots} li button`);

                dots.forEach((element, index) => {
                    element.dataset.testid = dotsTestId + index;
                });
            }
        } catch (e) {
            log.debug(`Carousel: addDotsTestIDs error: ${e}`);
        }
    };

    const builtinOptions: IBuildOption = {
        onInit: addDotsTestIDs,
        customPaging: getCustomPaging,
        speed: 300,
        waitForAnimate: true,
        focusOnSelect,
        accessibility: isAccessibilityEnabled,
        draggable: isDraggable,
        swipe: isSwipeEnabled,
        adaptiveHeight: isAdaptiveHeight,
        arrows: isControlArrowVisible,
        dots: isDotsVisible,
        infinite: isInfinite,
        dotsClass: cn(dotsClassName, styles.Dots, {[styles.DotsFocus]: isDotsFocusEnabled}),
        nextArrow: (
            <CarouselArrow
                ariaLabel={ariaLabelRight}
                testId={arrowNextTestId}
                customClassName={cn(arrowStyles.RightArrow, controlArrowClassName, controlArrowRightClassName)}
                isVisible={!isLastSlide || isInfinite}
                tabIndex={nextPreviousTabIndex}
            />
        ),
        prevArrow: (
            <CarouselArrow
                ariaLabel={ariaLabelLeft}
                customClassName={cn(arrowStyles.LeftArrow, controlArrowClassName, controlArrowLeftClassName)}
                testId={arrowPrevTestId}
                isVisible={!isFirstSlide || isInfinite}
                tabIndex={nextPreviousTabIndex}
            />
        ),
    };

    builtinOptions.beforeChange = (current: number, next: number) => onSlide(next);

    builtinOptions.afterChange = (index: number) => {
        helpers.runFunction(onAfterChange, index);
        onSlide(index);
        afterChange();
    };

    const mergedOptions = {...builtinOptions, ...options};

    useImperativeHandle(ref, () => ({
        slide: (index: number) => sliderRef.current.slickGoTo(index),
        next: () => sliderRef.current.slickNext(),
        prev: () => sliderRef.current.slickPrev(),
        slider: sliderRef.current,
    }));

    return (
        <Slider {...mergedOptions} ref={sliderRef}>
            {children}
        </Slider>
    );
});

Carousel.displayName = 'Carousel';

export default Carousel;
